/**
 * NaijaVault API Client
 * Shared client for Web, Admin, and Mobile apps
 */

import type {
  ApiResponse,
  AuthCredentials,
  AuthSession,
  RegisterData,
  User,
  UserProfile,
  Property,
  PropertyWithStats,
  PaginatedResponse,
  QueryParams,
  TicketWithProperty,
  TicketPurchase,
  TicketPurchaseResult,
  PaymentInitiation,
  Draw,
  DrawResult,
  Notification,
  DashboardStats,
  Transaction,
} from '@raffle/types';

export interface ApiClientConfig {
  baseUrl: string;
  getAccessToken?: () => string | null;
  onTokenExpired?: () => void;
  onUnauthorized?: () => void;
}

export class ApiClient {
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    const headers = new Headers(options.headers);
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

    // Add auth token if available
    const token = this.config.getAccessToken?.();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json() as ApiResponse<T>;

      // Handle token expiration
      if (response.status === 401) {
        if (data.error?.code === 'UNAUTHORIZED' && this.config.onTokenExpired) {
          this.config.onTokenExpired();
        } else if (this.config.onUnauthorized) {
          this.config.onUnauthorized();
        }
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed',
        },
      };
    }
  }

  private get<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    let queryString = '';
    if (params) {
      const cleaned = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      );
      if (Object.keys(cleaned).length) {
        queryString = '?' + new URLSearchParams(cleaned).toString();
      }
    }
    return this.request<T>(`${endpoint}${queryString}`);
  }

  private post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  private put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  private patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  private delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // ============================================
  // Auth Endpoints
  // ============================================

  auth = {
    register: (data: RegisterData) =>
      this.post<AuthSession>('/auth/register', data),

    login: (credentials: AuthCredentials) =>
      this.post<AuthSession>('/auth/login', credentials),

    refresh: (refreshToken: string) =>
      this.post<{ tokens: AuthSession['tokens'] }>('/auth/refresh', { refreshToken }),

    logout: () =>
      this.post<{ message: string }>('/auth/logout'),

    me: () =>
      this.get<UserProfile>('/auth/me'),

    updateMe: (data: { firstName?: string; lastName?: string; phone?: string | null; avatarUrl?: string | null }) =>
      this.patch<UserProfile>('/auth/me', data),

    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      this.post<{ message: string }>('/auth/change-password', data),
  };

  // ============================================
  // Properties Endpoints
  // ============================================

  properties = {
    list: (params?: QueryParams & { status?: string }) =>
      this.get<PaginatedResponse<Property>>('/properties', params as Record<string, string>),

    get: (id: string) =>
      this.get<PropertyWithStats>(`/properties/${id}`),

    // Admin only
    create: (data: Partial<Property>) =>
      this.post<Property>('/properties/admin', data),

    update: (id: string, data: Partial<Property>) =>
      this.put<Property>(`/properties/admin/${id}`, data),

    delete: (id: string) =>
      this.delete<{ message: string }>(`/properties/admin/${id}`),
  };

  // ============================================
  // Tickets Endpoints
  // ============================================

  tickets = {
    list: (params?: QueryParams & { propertyId?: string; status?: string }) =>
      this.get<PaginatedResponse<TicketWithProperty>>('/tickets', params as Record<string, string>),

    get: (id: string) =>
      this.get<TicketWithProperty>(`/tickets/${id}`),

    purchase: (data: TicketPurchase) =>
      this.post<TicketPurchaseResult>('/tickets/purchase', data),
  };

  // ============================================
  // Payments Endpoints
  // ============================================

  payments = {
    initiate: (data: { propertyId: string; quantity: number; callbackUrl: string }) =>
      this.post<PaymentInitiation>('/payments/initiate', data),

    verify: (reference: string) =>
      this.get<{ status: string; amount: number; reference: string }>(`/payments/verify/${reference}`),
  };

  // ============================================
  // Draws Endpoints
  // ============================================

  draws = {
    get: (propertyId: string) =>
      this.get<Draw>(`/draws/${propertyId}`),

    // Admin only
    execute: (propertyId: string) =>
      this.post<DrawResult>(`/draws/admin/execute/${propertyId}`),

    list: () =>
      this.get<{ items: Draw[] }>('/draws/admin/list'),
  };

  // ============================================
  // Notifications Endpoints
  // ============================================

  notifications = {
    list: (params?: QueryParams & { unreadOnly?: string }) =>
      this.get<PaginatedResponse<Notification>>('/notifications', params as Record<string, string>),

    unreadCount: () =>
      this.get<{ count: number }>('/notifications/unread-count'),

    markRead: (id: string) =>
      this.put<{ message: string }>(`/notifications/${id}/read`),

    markAllRead: () =>
      this.put<{ message: string }>('/notifications/read-all'),
  };

  // ============================================
  // Admin Endpoints
  // ============================================

  admin = {
    stats: () =>
      this.get<DashboardStats>('/admin/stats'),

    users: (params?: QueryParams & { role?: string }) =>
      this.get<PaginatedResponse<User>>('/admin/users', params as Record<string, string>),

    transactions: (params?: QueryParams & { status?: string; type?: string }) =>
      this.get<PaginatedResponse<Transaction>>('/admin/transactions', params as Record<string, string>),

    updateUserRole: (userId: string, role: string) =>
      this.put<{ message: string }>(`/admin/users/${userId}/role`, { role }),

    addUserCredits: (userId: string, credits: number, reason?: string) =>
      this.put<{ message: string }>(`/admin/users/${userId}/credits`, { credits, reason }),
  };
}

// Factory function for creating client
export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}
